import { z } from 'zod'

// Schema for creating a new bill
export const createBillSchema = z.object({
  type: z.enum(['payable', 'receivable'], {
    errorMap: () => ({ message: 'Tipo deve ser "Conta a Pagar" ou "Conta a Receber"' })
  }),
  document_number: z.string()
    .min(1, 'Número do documento é obrigatório')
    .max(50, 'Número do documento deve ter no máximo 50 caracteres'),
  description: z.string()
    .min(3, 'Descrição deve ter pelo menos 3 caracteres')
    .max(500, 'Descrição deve ter no máximo 500 caracteres'),
  original_amount: z.string()
    .min(1, 'Valor é obrigatório')
    .refine((val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0, {
      message: 'Valor deve ser maior que zero'
    }),
  supplier_id: z.string()
    .min(1, 'Fornecedor é obrigatório'),
  category_id: z.string().optional(),
  issue_date: z.string()
    .min(1, 'Data de emissão é obrigatória')
    .refine((date) => {
      const parsed = new Date(date)
      return !isNaN(parsed.getTime())
    }, 'Data de emissão inválida'),
  due_date: z.string()
    .min(1, 'Data de vencimento é obrigatória')
    .refine((date) => {
      const parsed = new Date(date)
      return !isNaN(parsed.getTime())
    }, 'Data de vencimento inválida'),
  notes: z.string().max(1000, 'Notas devem ter no máximo 1000 caracteres').optional()
}).refine((data) => {
  // Validate that due_date is not before issue_date
  const issue = new Date(data.issue_date)
  const due = new Date(data.due_date)
  return due >= issue
}, {
  message: 'Data de vencimento não pode ser anterior à data de emissão',
  path: ['due_date']
})

// Schema for payment
export const paymentSchema = z.object({
  account_id: z.string()
    .min(1, 'Conta bancária é obrigatória'),
  payment_method: z.enum(['pix', 'ted', 'boleto', 'dinheiro', 'cartao_credito', 'cartao_debito'], {
    errorMap: () => ({ message: 'Método de pagamento inválido' })
  }),
  payment_date: z.string()
    .min(1, 'Data de pagamento é obrigatória')
    .refine((date) => {
      const parsed = new Date(date)
      return !isNaN(parsed.getTime())
    }, 'Data de pagamento inválida'),
  notes: z.string().max(500, 'Notas devem ter no máximo 500 caracteres').optional()
})

// Schema for editing a bill (all fields optional)
export const editBillSchema = z.object({
  id: z.number().positive(),
  type: z.enum(['payable', 'receivable']).optional(),
  document_number: z.string().max(50).optional(),
  description: z.string().min(3).max(500).optional(),
  original_amount: z.string().refine((val) => !val || (!isNaN(parseFloat(val)) && parseFloat(val) > 0), {
    message: 'Valor deve ser maior que zero'
  }).optional(),
  supplier_id: z.string().optional(),
  category_id: z.string().optional(),
  issue_date: z.string().optional(),
  due_date: z.string().optional(),
  notes: z.string().max(1000).optional()
})

// Types inferred from schemas
export type CreateBillInput = z.infer<typeof createBillSchema>
export type PaymentInput = z.infer<typeof paymentSchema>
export type EditBillInput = z.infer<typeof editBillSchema>
